'use client';

import React, { useState, useTransition, useId } from 'react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

const servicesCovered = [
  {
    icon: 'TruckIcon' as const,
    title: 'Stretcher Transport',
    description: 'Ambulances with BLS-certified crew for hospital discharge, inter-facility transfers, and patients requiring lying-down transport.',
  },
  {
    icon: 'UserGroupIcon' as const,
    title: 'Wheelchair Transport',
    description: 'Mobility Assistance Vehicles (MAVs) with hydraulic lifts and secure tie-down systems for wheelchair-dependent patients.',
  },
  {
    icon: 'CalendarDaysIcon' as const,
    title: 'Recurring Scheduling',
    description: 'Standing schedules for dialysis, ongoing appointments, and regular transfers — planned in advance with your team.',
  },
];

const facilityTypes = [
  'Hospitals and hospital systems',
  'Nursing homes (SNFs)',
  'Assisted living facilities and retirement communities',
  'Dialysis centers',
  'Hospice programs',
  'Rehab and radiation treatment centers',
];

const accountSteps = [
  {
    step: '01',
    title: 'Call',
    description: 'Brief intake conversation about your facility\'s needs, volume, and workflow.',
  },
  {
    step: '02',
    title: 'Site Visit',
    description: 'We meet your team and learn your workflow — floor handoffs, scheduling patterns, communication preferences.',
  },
  {
    step: '03',
    title: 'Onboarding',
    description: 'Billing setup, communication protocols, and standing schedules established with your team.',
  },
  {
    step: '04',
    title: 'Live',
    description: 'Your team calls or messages dispatch as needed. One point of contact. We handle the rest.',
  },
];

interface B2BFormState {
  status: 'idle' | 'success' | 'error';
  message: string;
}

interface B2BFieldErrors {
  facilityName?: string;
  contactName?: string;
  role?: string;
  email?: string;
  phone?: string;
  transportVolume?: string;
  consent?: string;
}

const ALLOWED_VOLUMES = ['less-than-5', '5-20', '20-50', '50-plus'];

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) && email.length <= 254;
}

function isValidPhone(phone: string): boolean {
  return /^[\d\s\-().+]{7,20}$/.test(phone.trim());
}

function B2BInquiryForm() {
  const formId = useId();
  const [isPending, startTransition] = useTransition();
  const [formState, setFormState] = useState<B2BFormState>({ status: 'idle', message: '' });
  const [fieldErrors, setFieldErrors] = useState<B2BFieldErrors>({});

  const validate = (data: FormData): B2BFieldErrors => {
    const errors: B2BFieldErrors = {};
    const facilityName = (data.get('facilityName') as string || '').trim();
    const contactName = (data.get('contactName') as string || '').trim();
    const role = (data.get('role') as string || '').trim();
    const email = (data.get('email') as string || '').trim();
    const phone = (data.get('phone') as string || '').trim();
    const transportVolume = (data.get('transportVolume') as string || '').trim();
    const consent = data.get('consent');

    if (!facilityName || facilityName.length < 2) errors.facilityName = 'Facility name is required.';
    if (!contactName || contactName.length < 2) errors.contactName = 'Contact name is required.';
    if (!role || role.length < 2) errors.role = 'Role / title is required.';
    if (!email) errors.email = 'Email address is required.';
    else if (!isValidEmail(email)) errors.email = 'Enter a valid email address.';
    if (!phone) errors.phone = 'Phone number is required.';
    else if (!isValidPhone(phone)) errors.phone = 'Enter a valid phone number (e.g. (201) 555-0100).';
    if (!transportVolume || !ALLOWED_VOLUMES.includes(transportVolume)) errors.transportVolume = 'Please select a transport volume.';
    if (!consent) errors.consent = 'You must agree to be contacted to submit this inquiry.';

    return errors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const errors = validate(data);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    startTransition(async () => {
      try {
        const res = await fetch('/api/b2b-inquiry', {
          method: 'POST',
          body: JSON.stringify(Object.fromEntries(data)),
          headers: { 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          setFormState({ status: 'success', message: 'Thank you! Your partnership inquiry has been received. A member of our team will be in touch within one business day.' });
          form.reset();
        } else {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || 'Server error');
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Something went wrong.';
        setFormState({ status: 'error', message: msg === 'Server error' ? 'Something went wrong. Please call us directly at (551) 326-9573.' : msg });
      }
    });
  };

  if (formState.status === 'success') {
    return (
      <div className="bg-sage/10 border border-sage/40 rounded-2xl p-8 text-center" role="alert" aria-live="polite">
        <div className="w-14 h-14 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircleIcon" size={28} className="text-sage" variant="solid" />
        </div>
        <h3 className="font-display text-xl font-semibold text-primary mb-2">Inquiry Received!</h3>
        <p className="text-muted-foreground leading-relaxed">{formState.message}</p>
        <button className="btn-outline-navy mt-6 text-sm" onClick={() => setFormState({ status: 'idle', message: '' })}>
          Submit Another Inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-label="B2B partnership inquiry form" className="space-y-5">
      {formState.status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" role="alert" aria-live="assertive">
          <Icon name="ExclamationCircleIcon" size={18} className="text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{formState.message}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${formId}-facility`} className="form-label-base">
            Facility Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input id={`${formId}-facility`} name="facilityName" type="text" required placeholder="St. Mary's Medical Center"
            className={`form-input-base${fieldErrors.facilityName ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true" aria-invalid={!!fieldErrors.facilityName}
            aria-describedby={fieldErrors.facilityName ? `${formId}-facility-err` : undefined} maxLength={200} />
          {fieldErrors.facilityName && <p id={`${formId}-facility-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.facilityName}</p>}
        </div>
        <div>
          <label htmlFor={`${formId}-contact`} className="form-label-base">
            Contact Name <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input id={`${formId}-contact`} name="contactName" type="text" required placeholder="Jane Smith"
            className={`form-input-base${fieldErrors.contactName ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true" aria-invalid={!!fieldErrors.contactName}
            aria-describedby={fieldErrors.contactName ? `${formId}-contact-err` : undefined} maxLength={100} />
          {fieldErrors.contactName && <p id={`${formId}-contact-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.contactName}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${formId}-role`} className="form-label-base">
            Role / Title <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input id={`${formId}-role`} name="role" type="text" required placeholder="Discharge Coordinator"
            className={`form-input-base${fieldErrors.role ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true" aria-invalid={!!fieldErrors.role}
            aria-describedby={fieldErrors.role ? `${formId}-role-err` : undefined} maxLength={100} />
          {fieldErrors.role && <p id={`${formId}-role-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.role}</p>}
        </div>
        <div>
          <label htmlFor={`${formId}-email`} className="form-label-base">
            Email <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input id={`${formId}-email`} name="email" type="email" required autoComplete="email" placeholder="jane@facility.org"
            className={`form-input-base${fieldErrors.email ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true" aria-invalid={!!fieldErrors.email}
            aria-describedby={fieldErrors.email ? `${formId}-email-err` : undefined} maxLength={254} />
          {fieldErrors.email && <p id={`${formId}-email-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.email}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${formId}-phone`} className="form-label-base">
            Phone <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input id={`${formId}-phone`} name="phone" type="tel" required autoComplete="tel" placeholder="(201) 555-0100"
            className={`form-input-base${fieldErrors.phone ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true" aria-invalid={!!fieldErrors.phone}
            aria-describedby={fieldErrors.phone ? `${formId}-phone-err` : undefined} maxLength={30} />
          {fieldErrors.phone && <p id={`${formId}-phone-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.phone}</p>}
        </div>
        <div>
          <label htmlFor={`${formId}-volume`} className="form-label-base">
            Estimated Transport Volume / Week <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <select id={`${formId}-volume`} name="transportVolume" required
            className={`form-input-base${fieldErrors.transportVolume ? ' border-red-400 focus:ring-red-300' : ''}`}
            aria-required="true" aria-invalid={!!fieldErrors.transportVolume}
            aria-describedby={fieldErrors.transportVolume ? `${formId}-volume-err` : undefined} defaultValue="">
            <option value="" disabled>Select volume</option>
            <option value="less-than-5">&lt;5 transports/week</option>
            <option value="5-20">5–20 transports/week</option>
            <option value="20-50">20–50 transports/week</option>
            <option value="50-plus">50+ transports/week</option>
          </select>
          {fieldErrors.transportVolume && <p id={`${formId}-volume-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.transportVolume}</p>}
        </div>
      </div>

      <fieldset>
        <legend className="form-label-base mb-2">Services Needed</legend>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { value: 'stretcher', label: 'Stretcher' },
            { value: 'wheelchair', label: 'Wheelchair' },
            { value: 'ambulatory', label: 'Ambulatory' },
            { value: 'recurring', label: 'Recurring Schedule' },
          ].map((svc) => (
            <label key={svc.value} className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
              <input type="checkbox" name="servicesNeeded" value={svc.value} className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30" />
              {svc.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor={`${formId}-message`} className="form-label-base">
          Message <span className="text-muted-foreground text-xs">(optional)</span>
        </label>
        <textarea id={`${formId}-message`} name="message" rows={4}
          placeholder="Tell us about your facility's transport needs, current challenges, or any questions you have."
          className="form-input-base resize-none" maxLength={2000} />
      </div>

      <div className="flex items-start gap-3">
        <input type="checkbox" id={`${formId}-consent`} name="consent" required
          className="w-4 h-4 mt-0.5 rounded border-border text-primary focus:ring-primary/30 shrink-0"
          aria-required="true" aria-invalid={!!fieldErrors.consent}
          aria-describedby={fieldErrors.consent ? `${formId}-consent-err` : undefined} />
        <div>
          <label htmlFor={`${formId}-consent`} className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
            I agree to be contacted by Kapra Medical Transportation about this inquiry.{' '}
            <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          {fieldErrors.consent && <p id={`${formId}-consent-err`} className="text-xs text-red-600 mt-1" role="alert">{fieldErrors.consent}</p>}
        </div>
      </div>

      <button
        type="submit"
        disabled={isPending}
        className="btn-primary w-full justify-center py-4 text-base disabled:opacity-60 disabled:cursor-not-allowed"
        aria-label="Send partnership inquiry"
      >
        {isPending ? (
          <>
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Sending…
          </>
        ) : (
          <>
            <Icon name="PaperAirplaneIcon" size={18} />
            Send Partnership Inquiry
          </>
        )}
      </button>
    </form>
  );
}

export default function FacilitiesPageClient() {
  return (
    <>
      <Header />
      <main id="main-content" tabIndex={-1}>
        {/* Hero */}
        <section className="bg-primary pt-32 pb-20 px-6 md:px-10" aria-labelledby="facilities-heading">
          <div className="max-w-screen-xl mx-auto">
            <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'For Facilities' }]} />
            <span className="text-xs font-bold uppercase tracking-widest text-white/40 mb-4 block">For Facilities</span>
            <h1 id="facilities-heading" className="text-section-xl font-display font-light text-white mb-5 max-w-3xl">
              Outsource Your Patient Transport to a{' '}
              <span className="italic text-white/70">Reliable Local Partner</span>
            </h1>
            <p className="text-white/70 max-w-2xl text-base leading-relaxed mb-8">
              Kapra Medical Transportation serves hospitals, nursing homes, assisted living, dialysis centers, and hospice programs across Northern New Jersey. HIPAA-compliant, fully licensed, on-call when you need us.
            </p>
            <p className="text-white/70 max-w-2xl text-base leading-relaxed mb-8">
              Kapra also handles long distance medical transport for facilities that need to move patients beyond Northern New Jersey, including inter-facility transfers and out-of-state discharges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#partnership-form" className="btn-primary">
                <Icon name="BuildingOfficeIcon" size={18} />
                Send Partnership Inquiry
              </a>
              <a href="tel:+15513269573" className="btn-outline-white">
                <Icon name="PhoneIcon" size={18} />
                Call (551) 326-9573
              </a>
            </div>
          </div>
        </section>

        {/* Services We Cover */}
        <section className="py-20 px-6 md:px-10 bg-background" aria-labelledby="services-covered-heading">
          <div className="max-w-screen-xl mx-auto">
            <div className="mb-12">
              <span className="text-xs font-bold uppercase tracking-widest text-accent mb-3 block">What We Cover</span>
              <h2 id="services-covered-heading" className="font-display text-3xl md:text-4xl font-light text-primary">Services We Provide</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {servicesCovered.map((svc) => (
                <div key={svc.title} className="bg-card rounded-2xl border border-border p-7 hover:shadow-card hover:border-accent/30 transition-all">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <Icon name={svc.icon} size={22} className="text-primary" />
                  </div>
                  <h3 className="font-display text-lg font-medium text-primary mb-2">{svc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{svc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Facility Types + Response Time */}
        <section className="py-20 px-6 md:px-10 bg-secondary" aria-labelledby="facility-types-heading">
          <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-accent mb-3 block">Who We Serve</span>
              <h2 id="facility-types-heading" className="font-display text-3xl font-light text-primary mb-6">Facility Types We Partner With</h2>
              <ul className="space-y-3">
                {facilityTypes.map((type) => (
                  <li key={type} className="flex items-center gap-3 text-sm text-foreground">
                    <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                      <Icon name="CheckIcon" size={11} className="text-accent" />
                    </div>
                    {type}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-6">
              <div className="bg-card rounded-2xl border border-border p-7">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon name="ClockIcon" size={22} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-medium text-primary mb-1">Response Time Commitment</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Same-day response for most discharge and transfer requests during operating hours (Mon–Sat, 3 AM – 8 PM). For recurring schedules, planned in advance with your team.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-2xl border border-border p-7">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon name="CreditCardIcon" size={22} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-medium text-primary mb-2">Billing &amp; Insurance Handling</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Icon name="CheckIcon" size={14} className="text-accent mt-0.5 shrink-0" />
                        Direct billing to all major insurance, Medicaid, and Medicare
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckIcon" size={14} className="text-accent mt-0.5 shrink-0" />
                        Private pay arrangements for uninsured patients
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckIcon" size={14} className="text-accent mt-0.5 shrink-0" />
                        Your team doesn't chase paperwork — we handle it
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HIPAA + Why Kapra */}
        <section className="py-20 px-6 md:px-10 bg-background" aria-labelledby="compliance-heading">
          <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-accent mb-3 block">Compliance &amp; Staff</span>
              <h2 id="compliance-heading" className="font-display text-3xl font-light text-primary mb-6">HIPAA Compliance &amp; Trained Staff</h2>
              <ul className="space-y-3">
                {[
                  'HIPAA-compliant handoffs and documentation',
                  'BLS-certified EMTs on all stretcher transports',
                  'Drivers trained in patient handling, safe transfers, and dignified care',
                  'Background-checked, licensed, and insured',
                ].map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm text-foreground">
                    <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon name="ShieldCheckIcon" size={11} className="text-accent" />
                    </div>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-primary rounded-2xl p-8 text-white">
              <span className="text-xs font-bold uppercase tracking-widest text-white/40 mb-3 block">Why Kapra</span>
              <h3 className="font-display text-2xl font-light text-white mb-4">Family-Run. Locally-Rooted. Since 2013.</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">
                Family-run, locally-rooted, in business since 2013. Twelve vehicles. Five Northern NJ counties. One point of contact for your scheduling team.
              </p>
              <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
                <div className="text-center">
                  <p className="font-display text-2xl font-semibold text-white">12</p>
                  <p className="text-xs text-white/50 mt-1">Vehicles</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-2xl font-semibold text-white">5</p>
                  <p className="text-xs text-white/50 mt-1">NJ Counties</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-2xl font-semibold text-white">2013</p>
                  <p className="text-xs text-white/50 mt-1">Est.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Account Setup Process */}
        <section className="py-20 px-6 md:px-10 bg-secondary" aria-labelledby="account-setup-heading">
          <div className="max-w-screen-xl mx-auto">
            <div className="mb-12">
              <span className="text-xs font-bold uppercase tracking-widest text-accent mb-3 block">Getting Started</span>
              <h2 id="account-setup-heading" className="font-display text-3xl md:text-4xl font-light text-primary">Account Setup Process</h2>
              <p className="text-muted-foreground mt-3 max-w-xl text-sm">From first call to live service — typically within one week.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {accountSteps.map((step) => (
                <div key={step.step} className="bg-card rounded-2xl border border-border p-6 relative">
                  <span className="font-display text-4xl font-bold text-primary/10 absolute top-4 right-5">{step.step}</span>
                  <h3 className="font-display text-lg font-medium text-primary mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mid-page CTA */}
        <section className="py-14 px-6 md:px-10 bg-accent" aria-label="Partnership call to action">
          <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white text-lg font-light">
              Ready to discuss a <strong className="font-semibold">partnership</strong>? We'll respond within one business day.
            </p>
            <a href="tel:+15513269573" className="shrink-0 inline-flex items-center gap-2 bg-white text-accent font-semibold px-6 py-3 rounded-xl hover:bg-white/90 transition-colors" aria-label="Call (551) 326-9573">
              <Icon name="PhoneIcon" size={16} />
              Call (551) 326-9573
            </a>
          </div>
        </section>

        {/* Services we provide for facilities */}
        <section className="py-16 px-6 md:px-10 bg-background" aria-labelledby="facility-services-heading">
          <div className="max-w-screen-xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-widest text-accent mb-3 block">Our Services</span>
            <h2 id="facility-services-heading" className="text-2xl font-display font-semibold text-primary mb-8">
              Services we provide for facilities
            </h2>
            <ul className="space-y-3">
              <li>
                <a href="/services/hospital-discharge" className="text-primary font-medium hover:text-accent transition-colors underline-offset-2 hover:underline">
                  Hospital Discharge Transportation
                </a>
              </li>
              <li>
                <a href="/services/nursing-home-transportation" className="text-primary font-medium hover:text-accent transition-colors underline-offset-2 hover:underline">
                  Nursing Home Transportation
                </a>
              </li>
              <li>
                <a href="/services/assisted-living-transportation" className="text-primary font-medium hover:text-accent transition-colors underline-offset-2 hover:underline">
                  Assisted Living Transportation
                </a>
              </li>
              <li>
                <a href="/services/dialysis-transportation" className="text-primary font-medium hover:text-accent transition-colors underline-offset-2 hover:underline">
                  Dialysis Transportation
                </a>
              </li>
            </ul>
          </div>
        </section>

        {/* B2B Partnership Inquiry Form */}
        <section id="partnership-form" className="py-20 px-6 md:px-10 bg-background" aria-labelledby="inquiry-form-heading">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-3xl mx-auto">
              <div className="mb-10">
                <span className="text-xs font-bold uppercase tracking-widest text-accent mb-3 block">Get In Touch</span>
                <h2 id="inquiry-form-heading" className="font-display text-3xl md:text-4xl font-light text-primary mb-3">Partnership Inquiry</h2>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-xl">
                  Tell us about your facility and transport needs. A member of our team will follow up within one business day to schedule a brief intake call.
                </p>
              </div>
              <div className="bg-card rounded-3xl border border-border p-8 md:p-10 shadow-card">
                <B2BInquiryForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
